const navy = "#0f2137";
const amber = "#f59e0b";
const muted = "#64748b";
const border = "#e2e8f0";
const text = "#1e293b";

const sections = [
  {
    title: "Who we are",
    body: "Level operates levelfield.app. We help homeowners prepare for and evaluate contractor quotes.",
  },
  {
    title: "What we collect",
    body: null,
    list: [
      { label: "Email address", detail: "When you join our waitlist or create an account, we collect your email address. We use it to send product updates, early access invitations, and service-related communications." },
      { label: "Quote and project information", detail: "When you use Level's analysis tools, you may submit contractor quote text, project descriptions, or related information. This content is processed by our AI engine to generate comparisons and insights." },
    ],
    note: "We do not collect payment information directly — payments are handled by a third-party processor.",
  },
  {
    title: "How we use your information",
    body: "We do not sell your data. We do not use your data for advertising. We do not use your data to train AI models.",
    table: [
      { data: "Email address", purpose: "Waitlist and product communications via Mailchimp" },
      { data: "Quote text", purpose: "AI-powered analysis via the Anthropic API" },
    ],
  },
  {
    title: "Third-party services",
    body: null,
    vendors: [
      { name: "Anthropic", detail: "Quote text and project information you submit is processed by the Anthropic API to generate AI-powered analysis.", link: "https://www.anthropic.com/privacy", linkLabel: "Anthropic Privacy Policy" },
      { name: "Mailchimp (Intuit)", detail: "Email addresses are stored and managed through Mailchimp, which we use to send waitlist and product communications.", link: "https://mailchimp.com/legal/privacy/", linkLabel: "Mailchimp Privacy Policy" },
      { name: "Vercel", detail: "Our website is hosted on Vercel, which processes standard web request data as part of serving the site.", link: "https://vercel.com/legal/privacy-policy", linkLabel: "Vercel Privacy Policy" },
    ],
  },
  {
    title: "Data retention",
    body: "We retain your email address for as long as you are subscribed to communications from us. You may unsubscribe at any time using the link in any email we send. Quote and project data submitted for analysis is not stored beyond what is needed to complete the analysis.",
  },
  {
    title: "Your rights",
    body: "You may request access to, correction of, or deletion of your personal data at any time by emailing privacy@levelfield.app. We will respond within 30 days.",
  },
  {
    title: "Children",
    body: "Level is not directed at children under 13. We do not knowingly collect data from children.",
  },
  {
    title: "Changes to this policy",
    body: "We may update this policy as our product evolves. We will note the updated date at the top of this page. Continued use of Level after changes constitutes acceptance of the updated policy.",
  },
];

export default function Privacy() {
  return (
    <div style={{ fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", color: text, background: "#fff", minHeight: "100vh" }}>
      {/* Nav */}
      <nav style={{ display: "flex", alignItems: "center", padding: "18px 5%", borderBottom: `1px solid ${border}`, background: "#fff", position: "sticky", top: 0, zIndex: 100 }}>
        <a href="/" style={{ textDecoration: "none" }}>
          <div style={{ fontSize: "1.4rem", fontWeight: 900, color: navy, letterSpacing: 4, textTransform: "lowercase" }}>
            le<span style={{ color: amber }}>v</span>el
          </div>
          <span style={{ fontSize: "0.65rem", fontWeight: 500, color: muted, letterSpacing: "1.5px", textTransform: "uppercase", display: "block", marginTop: -2 }}>levelfield.app</span>
        </a>
      </nav>

      {/* Header */}
      <div style={{ background: navy, color: "#fff", padding: "48px 5% 40px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <p style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, color: amber, marginBottom: 12 }}>Legal</p>
          <h1 style={{ fontSize: "clamp(1.8rem,4vw,2.6rem)", fontWeight: 900, letterSpacing: -1, marginBottom: 12, lineHeight: 1.1 }}>Privacy Policy</h1>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem" }}>Effective date: May 17, 2026 · Last updated: May 17, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "48px 5% 80px" }}>
        {sections.map((sec, i) => (
          <div key={i} style={{ marginBottom: 40, paddingBottom: 40, borderBottom: i < sections.length - 1 ? `1px solid ${border}` : "none" }}>
            <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: navy, marginBottom: 14 }}>{sec.title}</h2>

            {sec.table && (
              <div style={{ border: `1px solid ${border}`, borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: muted, fontSize: "0.72rem", textTransform: "uppercase", borderBottom: `1px solid ${border}` }}>Data</th>
                      <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: muted, fontSize: "0.72rem", textTransform: "uppercase", borderBottom: `1px solid ${border}` }}>Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sec.table.map((row, j) => (
                      <tr key={j} style={{ borderBottom: j < sec.table.length - 1 ? `1px solid ${border}` : "none" }}>
                        <td style={{ padding: "10px 14px", fontWeight: 600 }}>{row.data}</td>
                        <td style={{ padding: "10px 14px", color: muted }}>{row.purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {sec.list && (
              <div style={{ marginBottom: 14 }}>
                {sec.list.map((item, j) => (
                  <div key={j} style={{ marginBottom: 14 }}>
                    <span style={{ fontWeight: 700, color: navy }}>{item.label} — </span>
                    <span style={{ color: muted, fontSize: "0.9rem", lineHeight: 1.7 }}>{item.detail}</span>
                  </div>
                ))}
              </div>
            )}

            {sec.vendors && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {sec.vendors.map((v, j) => (
                  <div key={j} style={{ background: "#f8fafc", border: `1px solid ${border}`, borderRadius: 10, padding: "14px 16px" }}>
                    <div style={{ fontWeight: 700, color: navy, marginBottom: 4 }}>{v.name}</div>
                    <p style={{ fontSize: "0.87rem", color: muted, lineHeight: 1.6, marginBottom: 8 }}>{v.detail}</p>
                    <a href={v.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.8rem", color: amber, fontWeight: 700, textDecoration: "none" }}>{v.linkLabel} →</a>
                  </div>
                ))}
              </div>
            )}

            {sec.body && <p style={{ fontSize: "0.9rem", color: muted, lineHeight: 1.8 }}>{sec.body}</p>}
            {sec.note && <p style={{ fontSize: "0.85rem", color: muted, lineHeight: 1.7, marginTop: 10 }}>{sec.note}</p>}
          </div>
        ))}

        {/* Contact block */}
        <div style={{ background: navy, borderRadius: 14, padding: "28px 24px", textAlign: "center" }}>
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem", marginBottom: 8 }}>Questions about this policy?</p>
          <a href="mailto:privacy@levelfield.app" style={{ color: amber, fontWeight: 800, fontSize: "1rem", textDecoration: "none" }}>privacy@levelfield.app</a>
        </div>
      </div>

      {/* Footer */}
      <footer style={{ padding: "24px 5%", borderTop: `1px solid ${border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: "1.1rem", fontWeight: 900, color: navy, letterSpacing: 4, textTransform: "lowercase" }}>le<span style={{ color: amber }}>v</span>el</div>
        </div>
        <p style={{ fontSize: "0.8rem", color: muted }}>© 2026 Level. Built for homeowners.</p>
        <p style={{ fontSize: "0.8rem", color: muted }}><a href="/privacy" style={{ color: muted }}>Privacy</a> · <a href="/terms" style={{ color: muted }}>Terms</a></p>
      </footer>
    </div>
  );
}